// Subset-sum algorithm for matching candidates by net amount.
// DFS with caps to prevent hangs on large candidate pools.

import { round2 } from '../../../calc.ts';

// Hard cap on the candidate pool fed into DFS. Set high enough that real
// vendor pools (a few hundred active txs) fit; MAX_ITERATIONS bounds the
// actual DFS cost. When truncation occurs, a warning is logged so the
// regression is visible instead of silently dropping matches beyond the cap.
const MAX_CANDIDATES = 1000;
const MAX_ITERATIONS = 500_000;

/**
 * Find a subset of candidates whose net amounts sum to targetNet.
 * Returns indices into the candidates array, or null if no subset found.
 *
 * Fast path: single exact match anywhere (no cap).
 * DFS path: sorts by net desc (reaches target faster), capped at MAX_CANDIDATES and MAX_ITERATIONS.
 *
 * Logs `[subsetSum]` warnings when MAX_CANDIDATES truncation OR MAX_ITERATIONS
 * timeout actually fires — these are conditions that can hide valid matches.
 */
/** Optional diagnostics the caller can read WITHOUT changing findSubset's return type
 *  (the locked subsetSum tests pin `number[] | null`).
 *  - `exhausted`: DFS hit MAX_ITERATIONS before a subset was found (a valid one may exist). */
export interface SubsetDiagnostics {
  exhausted?: boolean;
}

export function findSubset(
  candidates: { net: number }[],
  targetNet: number,
  diag?: SubsetDiagnostics,
): number[] | null {
  // Fast path: single exact match
  const exactIdx = candidates.findIndex(c => round2(c.net) === targetNet);
  if (exactIdx !== -1) return [exactIdx];

  if (candidates.length > MAX_CANDIDATES) {
    console.warn(
      `[subsetSum] candidate pool truncated: ${candidates.length} → ${MAX_CANDIDATES} (target=${targetNet}). Matches beyond index ${MAX_CANDIDATES} will not be found.`,
    );
  }

  // Sort by net descending — larger values first reaches target faster, drastically reducing iterations
  const indexed = candidates.slice(0, MAX_CANDIDATES).map((c, i) => ({ net: c.net, origIdx: i }));
  indexed.sort((a, b) => b.net - a.net);

  let bestOrig: number[] | null = null;
  let iterations = 0;
  let exhausted = false;

  const dfs = (i: number, acc: number, chosen: number[]) => {
    if (round2(acc) === targetNet) { bestOrig = chosen.map(j => indexed[j].origIdx); return; }
    if (bestOrig) return;
    if (++iterations > MAX_ITERATIONS) { exhausted = true; return; }
    if (i >= indexed.length) return;
    for (let j = i; j < indexed.length; j++) {
      const next = round2(acc + indexed[j].net);
      if (next > targetNet) continue;
      chosen.push(j);
      dfs(j + 1, next, chosen);
      if (bestOrig || iterations > MAX_ITERATIONS) return;
      chosen.pop();
    }
  };

  dfs(0, 0, []);

  if (exhausted && !bestOrig) {
    console.warn(
      `[subsetSum] DFS exhausted ${MAX_ITERATIONS} iterations without finding a subset (target=${targetNet}, n=${indexed.length}). Treating as no-match — but a valid subset may exist.`,
    );
    if (diag) diag.exhausted = true;
  }

  return bestOrig;
}

/** Find a SECOND subset that sums to targetNet using indices DISJOINT from `exclude`
 *  (finding 12 ambiguity check). Returns the runner-up index list or null. Cheap: it
 *  reuses findSubset on the candidate set with the first solution's members removed. */
export function findDisjointSubset(
  candidates: { net: number }[],
  targetNet: number,
  exclude: number[],
): number[] | null {
  const excludeSet = new Set(exclude);
  const remaining: { net: number; origIdx: number }[] = [];
  candidates.forEach((c, i) => { if (!excludeSet.has(i)) remaining.push({ net: c.net, origIdx: i }); });
  const found = findSubset(remaining, targetNet);
  return found ? found.map(i => remaining[i].origIdx) : null;
}
